import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Sparkles, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { API, StatusPill, intervalText, fmtDate } from './fleetShared';
import { DatePickerField } from '../DatePickerField';

const INPUT = 'w-full px-2 py-1.5 border border-slate-300 rounded text-sm text-slate-900 outline-none focus:border-blue-500';
const L = ({ label, children }) => <div><label className="block text-[11px] font-medium text-slate-600 mb-0.5">{label}</label>{children}</div>;

// Time period stored as months (months / years) or days (days / weeks)
const periodFromItem = (item) => {
  const mo = Number(item?.interval_months) || 0, days = Number(item?.interval_days) || 0;
  if (mo && mo % 12 === 0) return { n: mo / 12, unit: 'years' };
  if (mo) return { n: mo, unit: 'months' };
  if (days && days % 7 === 0) return { n: days / 7, unit: 'weeks' };
  if (days) return { n: days, unit: 'days' };
  return { n: '', unit: 'months' };
};
const periodToFields = ({ n, unit }) => {
  const v = Number(n) || 0;
  if (!v) return { interval_months: '', interval_days: '' };
  if (unit === 'years') return { interval_months: v * 12, interval_days: '' };
  if (unit === 'months') return { interval_months: v, interval_days: '' };
  if (unit === 'weeks') return { interval_months: '', interval_days: v * 7 };
  return { interval_months: '', interval_days: v };
};

function ItemForm({ unit, meta, item, onDone, onCancel }) {
  const kind = unit.meter?.kind;
  const [f, setF] = useState(() => ({
    item_type: item?.item_type || 'oil_change', label: item?.label || '', interval_km: item?.interval_km ?? '', interval_hours: item?.interval_hours ?? '',
    last_done_date: item?.last_done_date || '', last_done_km: item?.last_done_km ?? '',
    last_done_hours: item?.last_done_hours ?? '', notes: item?.notes || '',
    notify_on_service: item ? item.notify_on_service : null,
  }));
  const [period, setPeriod] = useState(() => periodFromItem(item));
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const [saving, setSaving] = useState(false);
  const typeMeta = (meta?.item_types || []).find(t => t.value === f.item_type);
  const notifyDefault = !!typeMeta?.notify;
  const notify = f.notify_on_service == null ? notifyDefault : f.notify_on_service;
  const noSchedule = !Number(f.interval_km) && !Number(f.interval_hours) && !Number(period.n);
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...f, ...periodToFields(period), notify_on_service: f.notify_on_service == null ? null : f.notify_on_service };
      if (item) await axios.put(`${API}/items/${item.id}`, body);
      else await axios.post(`${API}/equipment/${unit.id}/items`, body);
      toast.success(item ? 'Item updated' : 'Item added'); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };
  const typeLabel = typeMeta?.label || '';
  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="fleet-item-form">
      <L label="Maintenance item">
        <select value={f.item_type} onChange={e => { set('item_type', e.target.value); set('notify_on_service', null); }} data-testid="fleet-item-type" className={INPUT}>
          {(meta?.item_types || []).map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </L>
      <L label="Label (optional)"><input value={f.label} onChange={e => set('label', e.target.value)} placeholder={typeLabel} data-testid="fleet-item-label" className={INPUT} /></L>
      {kind === 'km' && <L label="Every … km (blank = none)"><input type="number" min="0" value={f.interval_km} onChange={e => set('interval_km', e.target.value)} data-testid="fleet-item-interval-km" className={INPUT} placeholder="e.g. 10000" /></L>}
      {kind === 'hours' && <L label="Every … hours (blank = none)"><input type="number" min="0" value={f.interval_hours} onChange={e => set('interval_hours', e.target.value)} data-testid="fleet-item-interval-hours" className={INPUT} placeholder="e.g. 250" /></L>}
      <L label="Every … time period (blank = none)">
        <div className="flex gap-1">
          <input type="number" min="0" value={period.n} onChange={e => setPeriod(p => ({ ...p, n: e.target.value }))} data-testid="fleet-item-period-n" className={INPUT} placeholder="e.g. 6" />
          <select value={period.unit} onChange={e => setPeriod(p => ({ ...p, unit: e.target.value }))} data-testid="fleet-item-period-unit" className={`${INPUT} w-28`}>
            <option value="days">days</option><option value="weeks">weeks</option><option value="months">months</option><option value="years">years</option>
          </select>
        </div>
      </L>
      <L label="Last done (date)"><DatePickerField value={f.last_done_date} onChange={v => set('last_done_date', v)} className={INPUT} testid="fleet-item-last-date" /></L>
      {kind === 'km' && <L label="Last done at … km"><input type="number" value={f.last_done_km} onChange={e => set('last_done_km', e.target.value)} data-testid="fleet-item-last-km" className={INPUT} /></L>}
      {kind === 'hours' && <L label="Last done at … hrs"><input type="number" value={f.last_done_hours} onChange={e => set('last_done_hours', e.target.value)} data-testid="fleet-item-last-hours" className={INPUT} /></L>}
      <div className="col-span-2 md:col-span-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]"><L label="Notes"><input value={f.notes} onChange={e => set('notes', e.target.value)} className={INPUT} placeholder="e.g. synthetic 5W-30, Brant County Ford" /></L></div>
        <label className="flex items-center gap-1.5 text-xs text-slate-700 pb-1.5 cursor-pointer" title="Push + in-app note to the fleet managers / office each time this is logged">
          <input type="checkbox" checked={notify} onChange={e => set('notify_on_service', e.target.checked)} data-testid="fleet-item-notify" className="rounded" />
          Notify office when logged{f.notify_on_service == null && <span className="text-slate-400"> (default for this type)</span>}
        </label>
        <button onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
        <button onClick={save} disabled={saving} data-testid="fleet-item-save" className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium disabled:opacity-50">{saving ? 'Saving…' : item ? 'Save' : 'Add item'}</button>
      </div>
      {noSchedule && <p className="col-span-2 md:col-span-4 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1" data-testid="fleet-item-no-schedule">No interval set — this item is tracked "as needed": it never comes due, but every time it's logged shows in the history.</p>}
    </div>
  );
}

const RANK = { overdue: 0, due_soon: 1, ok: 2, no_baseline: 3, as_needed: 4, error: 5 };
const num = (v) => (v == null || v === '' ? null : Number(v));
// days-until-due proxy across km / hours / date so "Next due" sorts most-urgent first (≈60 km or 8 hrs a day)
const urgency = (it) => Math.min(it.remaining_days ?? Infinity, it.remaining_km != null ? it.remaining_km / 60 : Infinity, it.remaining_hours != null ? it.remaining_hours / 8 : Infinity);
const SORT_VAL = {
  label: (it) => (it.label || '').toLowerCase(),
  interval: (it) => [num(it.interval_km) ?? num(it.interval_hours), num(it.interval_months)],
  last: (it) => [it.last_done_date || null, num(it.last_done_km) ?? num(it.last_done_hours)],
  next: (it) => { const u = urgency(it); return u === Infinity ? null : u; },
  status: (it) => [RANK[it.status] ?? 9, (() => { const u = urgency(it); return u === Infinity ? null : u; })()],
};
const cmp = (a, b) => {
  if (Array.isArray(a)) { for (let i = 0; i < a.length; i++) { const c = cmp(a[i], b[i]); if (c) return c; } return 0; }
  if (a == null && b == null) return 0;
  if (a == null) return 1;   // blanks always last
  if (b == null) return -1;
  return a < b ? -1 : a > b ? 1 : 0;
};
const sortItems = (items, sort) => {
  if (!sort.key) return items;
  const val = SORT_VAL[sort.key];
  return items.map((it, i) => [it, i]).sort(([a, ai], [b, bi]) => {
    const va = val(a), vb = val(b);
    const blankA = Array.isArray(va) ? va[0] == null : va == null, blankB = Array.isArray(vb) ? vb[0] == null : vb == null;
    if (blankA !== blankB) return blankA ? 1 : -1;
    return (cmp(va, vb) * (sort.dir === 'desc' ? -1 : 1)) || ai - bi;
  }).map(([it]) => it);
};

const Th = ({ col, label, sort, onSort, className = '' }) => {
  const on = sort.key === col;
  const Icon = !on ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-2 py-2 text-left font-semibold ${className}`} aria-sort={on ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" onClick={() => onSort(col)} data-testid={`fleet-plan-sort-${col}`} className={`inline-flex items-center gap-1 hover:text-slate-900 ${on ? 'text-blue-700' : ''}`} title={`Sort by ${label.toLowerCase()}`}>
        {label} <Icon className={`w-3 h-3 ${on ? '' : 'opacity-40'}`} />
      </button>
    </th>
  );
};

export default function FleetPlanTab({ unit, meta, onChanged, onLogService }) {
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const kind = unit.meter?.kind;
  const onSort = (key) => setSort(s => (s.key !== key ? { key, dir: 'asc' } : s.dir === 'asc' ? { key, dir: 'desc' } : { key: null, dir: 'asc' }));
  const rows = useMemo(() => sortItems(unit.items || [], sort), [unit.items, sort]);

  const applyTemplate = async () => {
    try { const r = await axios.post(`${API}/equipment/${unit.id}/apply-template`, {}); toast.success(`${r.data.added} default item(s) added`); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'No default plan for this type'); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Remove "${it.label}" from the plan?`)) return;
    try { await axios.delete(`${API}/items/${it.id}`); toast.success('Removed'); onChanged(); } catch { toast.error('Delete failed'); }
  };
  const nextDue = (it) => [
    it.next_due_km != null ? `${it.next_due_km.toLocaleString()} km` : null,
    it.next_due_hours != null ? `${it.next_due_hours} hrs` : null,
    it.next_due_date ? fmtDate(it.next_due_date) : null,
  ].filter(Boolean).join(' or ') || '—';
  const lastDone = (it) => [
    it.last_done_date ? fmtDate(it.last_done_date) : null,
    kind === 'km' && it.last_done_km != null ? `${Number(it.last_done_km).toLocaleString()} km` : null,
    kind === 'hours' && it.last_done_hours != null ? `${it.last_done_hours} hrs` : null,
  ].filter(Boolean).join(' @ ') || 'never recorded';

  return (
    <div className="space-y-3">
      {unit.items.length === 0 && (
        <div className="text-center py-6 text-slate-500 text-sm bg-slate-50 rounded-lg" data-testid="fleet-plan-empty">
          No maintenance plan yet for this unit.
          {unit.can_edit && unit.template && (
            <div className="mt-3"><button onClick={applyTemplate} data-testid="fleet-apply-template" className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"><Sparkles className="w-4 h-4" /> Apply default {unit.template.replace('_', ' ')} plan</button></div>
          )}
        </div>
      )}
      {unit.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <Th col="label" label="Item" sort={sort} onSort={onSort} className="sticky left-0 z-[2] bg-slate-50 min-w-[170px]" />
              <Th col="interval" label="Interval" sort={sort} onSort={onSort} />
              <Th col="last" label="Last done" sort={sort} onSort={onSort} />
              <Th col="next" label="Next due" sort={sort} onSort={onSort} />
              <Th col="status" label="Status" sort={sort} onSort={onSort} />
              <th className="px-2 py-2 text-right font-semibold">{sort.key && <button onClick={() => setSort({ key: null, dir: 'asc' })} data-testid="fleet-plan-sort-clear" className="text-[10px] text-slate-500 hover:text-slate-900 font-normal">clear sort</button>}</th>
            </tr></thead>
            <tbody>
              {rows.map(it => (
                <tr key={it.id} className="border-t border-slate-100 group" data-testid={`fleet-plan-row-${it.id}`}>
                  <td className="px-2 py-2 font-medium text-slate-800 sticky left-0 z-[1] bg-white min-w-[170px] shadow-[inset_-1px_0_0_#e2e8f0]">{it.label}{it.notes && <div className="text-[10px] text-slate-500">{it.notes}</div>}</td>
                  <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{intervalText(it)}</td>
                  <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{lastDone(it)}</td>
                  <td className="px-2 py-2 text-slate-700 whitespace-nowrap">{nextDue(it)}</td>
                  <td className="px-2 py-2"><StatusPill status={it.status}>{it.status === 'no_baseline' ? 'Needs baseline' : it.status === 'as_needed' ? 'As needed' : it.summary}</StatusPill>{it.notify_on_service && <span className="ml-1 text-[10px] text-slate-500" title="Office is notified when this is logged">🔔</span>}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {unit.can_edit && (
                      <span className="inline-flex gap-1">
                        <button onClick={onLogService} data-testid={`fleet-plan-log-${it.id}`} className="px-2 py-1 text-[11px] bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded">Log service</button>
                        <button onClick={() => setEditing(it)} data-testid={`fleet-plan-edit-${it.id}`} className="p-1 text-slate-500 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(it)} data-testid={`fleet-plan-delete-${it.id}`} className="p-1 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <ItemForm unit={unit} meta={meta} item={editing === 'new' ? null : editing} onDone={() => { setEditing(null); onChanged(); }} onCancel={() => setEditing(null)} />}
      {unit.can_edit && !editing && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setEditing('new')} data-testid="fleet-add-item" className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"><Plus className="w-4 h-4" /> Add maintenance item</button>
          {unit.items.length > 0 && unit.template && <button onClick={applyTemplate} data-testid="fleet-apply-template" className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm"><Sparkles className="w-4 h-4" /> Add missing defaults</button>}
        </div>
      )}
    </div>
  );
}
